import {
  usePatientFamilyHistories,
  usePatientMedicalHistories,
  usePatientSocialHistories,
  usePatientSurgicalHistories
} from '@hook'
import { FamilyDataMock, MedDataMock, SocialDataMock, SurgeryDataMock } from '@type/history.type'
import { Tab2 } from '@ui'
import { useMemo, useState } from 'react'
import FamilyHistoryTable from './FamilyHistoryTable'
import MedicalHistoryTable from './MedicalHistoryTable'
import SocialHistoryTable from './SocialHistoryTable'
import SurgicalHistoryTable from './SurgicalHistoryTable'
import HistoryTableLayout from './tableLayout'

type HistoryTab<TRecord> = {
  getSearchText: (record: TRecord) => string
  getStatus?: (record: TRecord) => string | undefined
  isLoading?: boolean
  label: string
  records: TRecord[]
  renderTable: (records: TRecord[]) => JSX.Element
  searchKey: string
  value: string
}

type TabValue = 'medical' | 'family' | 'surgical' | 'social'

type MedicalTab = HistoryTab<MedDataMock> & { value: 'medical' }
type FamilyTab = HistoryTab<FamilyDataMock> & { value: 'family' }
type SurgicalTab = HistoryTab<SurgeryDataMock> & { value: 'surgical' }
type SocialTab = HistoryTab<SocialDataMock> & { value: 'social' }

type AnyHistoryTab = MedicalTab | FamilyTab | SurgicalTab | SocialTab

export default function History() {
  const [activeTab, setActiveTab] = useState<TabValue>('medical')
  const { included, isLoading: medicalIsLoading, medicals } = usePatientMedicalHistories()
  const { families, isLoading: familyIsLoading } = usePatientFamilyHistories()
  const { isLoading: surgicalIsLoading, surgeries } = usePatientSurgicalHistories()
  const { isLoading: socialIsLoading, socials } = usePatientSocialHistories()

  const historyTabs = useMemo<[MedicalTab, FamilyTab, SurgicalTab, SocialTab]>(() => {
    const medical: MedicalTab = {
      getSearchText: record => `${record.attributes.condition || ''} ${record.attributes.title || ''}`,
      getStatus: record => record.meta.status,
      isLoading: medicalIsLoading,
      label: 'Medical',
      records: medicals || [],
      renderTable: records => (
        <MedicalHistoryTable included={included} isLoading={medicalIsLoading} records={records} />
      ),
      searchKey: 'medicalHistorySearch',
      value: 'medical'
    }

    const family: FamilyTab = {
      getSearchText: record =>
        `${record.attributes.relationship || ''} ${record.attributes.full_name || ''} ${record.attributes.medical_conditions?.join(' ') || ''}`,
      isLoading: familyIsLoading,
      label: 'Family',
      records: families || [],
      renderTable: records => <FamilyHistoryTable isLoading={familyIsLoading} records={records} />,
      searchKey: 'familyHistorySearch',
      value: 'family'
    }

    const surgical: SurgicalTab = {
      getSearchText: record =>
        `${record.attributes.surgery_type || ''} ${record.attributes.surgery_date || ''} ${record.attributes.location || ''}`,
      isLoading: surgicalIsLoading,
      label: 'Surgical',
      records: surgeries || [],
      renderTable: records => <SurgicalHistoryTable isLoading={surgicalIsLoading} records={records} />,
      searchKey: 'surgicalHistorySearch',
      value: 'surgical'
    }

    const social: SocialTab = {
      getSearchText: record =>
        `${record.attributes.social_type || ''} ${record.attributes.social_name || ''} ${record.attributes.status || ''}`,
      getStatus: record => record.attributes.status,
      isLoading: socialIsLoading,
      label: 'Social',
      records: socials || [],
      renderTable: records => <SocialHistoryTable isLoading={socialIsLoading} records={records} />,
      searchKey: 'socialHistorySearch',
      value: 'social'
    }

    return [medical, family, surgical, social]
  }, [
    families,
    familyIsLoading,
    included,
    medicalIsLoading,
    medicals,
    socialIsLoading,
    socials,
    surgicalIsLoading,
    surgeries
  ])

  const [medicalTab, familyTab, surgicalTab, socialTab] = historyTabs

  const handleSetActiveTab = (value: string) => setActiveTab(value as TabValue)

  const historyTable = (() => {
    switch (activeTab) {
      case 'family':
        return (
          <HistoryTableLayout<FamilyDataMock>
            getSearchText={familyTab.getSearchText}
            getStatus={familyTab.getStatus}
            key={familyTab.value}
            records={familyTab.records}
            searchKey={familyTab.searchKey}
            title={familyTab.label}
          >
            {records => familyTab.renderTable(records)}
          </HistoryTableLayout>
        )
      case 'surgical':
        return (
          <HistoryTableLayout<SurgeryDataMock>
            getSearchText={surgicalTab.getSearchText}
            getStatus={surgicalTab.getStatus}
            key={surgicalTab.value}
            records={surgicalTab.records}
            searchKey={surgicalTab.searchKey}
            title={surgicalTab.label}
          >
            {records => surgicalTab.renderTable(records)}
          </HistoryTableLayout>
        )
      case 'social':
        return (
          <HistoryTableLayout<SocialDataMock>
            getSearchText={socialTab.getSearchText}
            getStatus={socialTab.getStatus}
            key={socialTab.value}
            records={socialTab.records}
            searchKey={socialTab.searchKey}
            title={socialTab.label}
          >
            {records => socialTab.renderTable(records)}
          </HistoryTableLayout>
        )
      case 'medical':
      default:
        return (
          <HistoryTableLayout<MedDataMock>
            getSearchText={medicalTab.getSearchText}
            getStatus={medicalTab.getStatus}
            key={medicalTab.value}
            records={medicalTab.records}
            searchKey={medicalTab.searchKey}
            title={medicalTab.label}
          >
            {records => medicalTab.renderTable(records)}
          </HistoryTableLayout>
        )
    }
  })()

  return (
    <div className="flex min-h-0 flex-col gap-5">
      <div className="w-max max-w-full">
        <Tab2 activeTab={activeTab} background="#FFFFFF" setActiveTab={handleSetActiveTab} tabs={historyTabs} />
      </div>

      {historyTable}
    </div>
  )
}
